import React, { useEffect, useMemo, useState } from "react";
import { Search, Eye, Download, ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import { getAllFeedbacks } from "../../../../services/supportService";

const ITEMS_PER_PAGE = 10;

const RecentFeedbacks = () => {
  const [filters, setFilters] = useState({
    search: '',
    rating: '',
    platform: '',
    languagePair: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    total: 0
  });
  const [apiStats, setApiStats] = useState({
    total_feedback: 0,
    total_feedback_liked: 0,
    total_feedback_disliked: 0
  });

  // Helper function to format feedback text based on issue type
  const formatFeedbackText = (feedbackText, isLiked) => {
    // Predefined issue labels (exact matches from the feedback component)
    const predefinedLabels = [
      'The meaning of the translation is incorrect',
      'Parts of the text were missing or not translated',
      'The phrasing sounds unnatural or awkward',
      'Cultural or local context was not respected'
    ];

    // If no feedback text, return dash
    if (!feedbackText || feedbackText === '-') {
      return '-';
    }

    // Only process negative feedback (isLiked === false)
    if (isLiked === false) {
      // Check if feedback_text matches any predefined label (case-insensitive)
      const matchesPredefinedLabel = predefinedLabels.some(
        label => feedbackText.toLowerCase() === label.toLowerCase()
      );

      // If it doesn't match any predefined label, it's "Other" type
      if (!matchesPredefinedLabel) {
        // Check if it already starts with "Other:"
        if (!feedbackText.toLowerCase().startsWith('other:')) {
          return `Other: ${feedbackText}`;
        }
      }
    }

    return feedbackText;
  };

  // Helper function to map API feedback to component format
  const mapFeedbackToComponent = (apiFeedback) => {
    const userId = apiFeedback.userId || {};
    const fullName = apiFeedback.full_name ||
      (userId.firstName && userId.lastName
        ? `${userId.firstName} ${userId.lastName}`
        : userId.firstName || userId.lastName || 'Unknown User');
    const email = apiFeedback.email || userId.email || '-';

    // Extract source and target languages from api_endpoint or use defaults
    // Format might be like "translate/en/yo" or similar
    let sourceLanguage = "English";
    let targetLanguage = "Yoruba";
    if (apiFeedback.api_endpoint) {
      // Try to parse language codes from endpoint
      const langMatch = apiFeedback.api_endpoint.match(/\/([a-z]{2})\/([a-z]{2})/i);
      if (langMatch) {
        const langMap = {
          'en': 'English',
          'yo': 'Yoruba',
          'sw': 'Swahili',
          'pcm': 'Nigerian Pidgin'
        };
        sourceLanguage = langMap[langMatch[1]?.toLowerCase()] || sourceLanguage;
        targetLanguage = langMap[langMatch[2]?.toLowerCase()] || targetLanguage;
      }
    }

    // Format date
    const date = apiFeedback.timestamp || apiFeedback.createdAt
      ? new Date(apiFeedback.timestamp || apiFeedback.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      : '-';

    // Format feedback text based on issue type
    const formattedFeedbackText = formatFeedbackText(apiFeedback.feedback_text, apiFeedback.is_liked);

    return {
      id: apiFeedback._id || apiFeedback.id,
      name: fullName,
      email: email,
      rating: apiFeedback.is_liked === true ? "positive" : "negative",
      platform: apiFeedback.platform || "-",
      issueReason: apiFeedback.issue_reason || "-",
      comment: formattedFeedbackText,
      date: date,
      sourceLanguage: apiFeedback.sourceLanguage || sourceLanguage,
      targetLanguage: apiFeedback.targetLanguage || targetLanguage,
      inputText: apiFeedback.input || "-",
      outputText: apiFeedback.output || "-",
      // Keep full data for modal
      fullEmail: email,
      fullComment: formattedFeedbackText
    };
  };

  // Fetch feedbacks from API
  useEffect(() => {
    const fetchFeedbacks = async () => {
      setLoading(true);
      setError(null);
      try {
        // Map component filters to API params
        const apiParams = {
          page: currentPage,
          limit: ITEMS_PER_PAGE,
          search: filters.search || '',
          platform: filters.platform || '',
          is_liked: filters.rating === 'positive' ? 'true' : filters.rating === 'negative' ? 'false' : '',
          api_endpoint: filters.languagePair || ''
        };

        const response = await getAllFeedbacks(apiParams);

        if (response.success && response.feedbacks) {
          const mappedFeedbacks = response.feedbacks.map(mapFeedbackToComponent);
          setFeedbacks(mappedFeedbacks);
          setPagination({
            totalPages: response.pagination?.totalPages || 1,
            currentPage: response.pagination?.currentPage || 1,
            total: response.pagination?.total || 0
          });
          // Store stats from API response
          if (response.stats) {
            setApiStats(response.stats);
          }
        } else {
          setError('Failed to fetch feedbacks');
          setFeedbacks([]);
        }
      } catch (err) {
        console.error('Error fetching feedbacks:', err);
        setError(err.message || 'Failed to fetch feedbacks');
        setFeedbacks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbacks();
  }, [currentPage, filters.search, filters.platform, filters.rating, filters.languagePair]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Helper function to convert feedbacks to CSV
  const convertToCSV = (feedbacksData) => {
    if (!feedbacksData || feedbacksData.length === 0) {
      return '';
    }

    // CSV Headers
    const headers = [
      'Name',
      'Email',
      'Rating',
      'Platform',
      'Source Language',
      'Target Language',
      'Input Text',
      'Output Text',
      'Issue Reason',
      'Comment',
      'Date'
    ];

    // Convert data to CSV rows
    const rows = feedbacksData.map(feedback => {
      // Escape commas and quotes in CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      return [
        escapeCSV(feedback.name || feedback.fullName || '-'),
        escapeCSV(feedback.fullEmail || feedback.email || '-'),
        escapeCSV(feedback.rating || '-'),
        escapeCSV(feedback.platform || '-'),
        escapeCSV(feedback.sourceLanguage || '-'),
        escapeCSV(feedback.targetLanguage || '-'),
        escapeCSV(feedback.inputText || '-'),
        escapeCSV(feedback.outputText || '-'),
        escapeCSV(feedback.issueReason || '-'),
        escapeCSV(feedback.fullComment || feedback.comment || '-'),
        escapeCSV(feedback.date || '-')
      ].join(',');
    });

    // Combine headers and rows
    return [headers.join(','), ...rows].join('\n');
  };

  // Helper function to download CSV file
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportAll = async () => {
    try {
      setExporting(true);
      setError(null);

      // Fetch all feedbacks with current filters applied
      // Use a high limit to get all results, or fetch multiple pages if needed
      const apiParams = {
        page: 1,
        limit: 10000, // High limit to get all feedbacks
        search: filters.search || '',
        platform: filters.platform || '',
        is_liked: filters.rating === 'positive' ? 'true' : filters.rating === 'negative' ? 'false' : '',
        api_endpoint: filters.languagePair || ''
      };

      const response = await getAllFeedbacks(apiParams);

      if (response.success && response.feedbacks) {
        // Map API feedbacks to export format
        const exportFeedbacks = response.feedbacks.map(apiFeedback => {
          const userId = apiFeedback.userId || {};
          const fullName = apiFeedback.full_name ||
            (userId.firstName && userId.lastName
              ? `${userId.firstName} ${userId.lastName}`
              : userId.firstName || userId.lastName || 'Unknown User');
          const email = apiFeedback.email || userId.email || '-';

          // Extract languages
          let sourceLanguage = "English";
          let targetLanguage = "Yoruba";
          if (apiFeedback.api_endpoint) {
            const langMatch = apiFeedback.api_endpoint.match(/\/([a-z]{2})\/([a-z]{2})/i);
            if (langMatch) {
              const langMap = {
                'en': 'English',
                'yo': 'Yoruba',
                'sw': 'Swahili',
                'pcm': 'Nigerian Pidgin'
              };
              sourceLanguage = langMap[langMatch[1]?.toLowerCase()] || sourceLanguage;
              targetLanguage = langMap[langMatch[2]?.toLowerCase()] || targetLanguage;
            }
          }

          // Format date
          const date = apiFeedback.timestamp || apiFeedback.createdAt
            ? new Date(apiFeedback.timestamp || apiFeedback.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            })
            : '-';

          // Format feedback text using the same helper function
          const formattedFeedbackText = formatFeedbackText(apiFeedback.feedback_text, apiFeedback.is_liked);

          return {
            name: fullName,
            fullName: fullName,
            email: email,
            fullEmail: email,
            rating: apiFeedback.is_liked === true ? "positive" : "negative",
            platform: apiFeedback.platform || "-",
            sourceLanguage: apiFeedback.sourceLanguage || sourceLanguage,
            targetLanguage: apiFeedback.targetLanguage || targetLanguage,
            inputText: apiFeedback.input || "-",
            outputText: apiFeedback.output || "-",
            issueReason: apiFeedback.issue_reason || "-",
            comment: formattedFeedbackText,
            fullComment: formattedFeedbackText,
            date: date
          };
        });

        // Convert to CSV
        const csvContent = convertToCSV(exportFeedbacks);

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `feedbacks-export-${timestamp}.csv`;

        // Download the file
        downloadCSV(csvContent, filename);
      } else {
        setError('No feedbacks found to export');
      }
    } catch (err) {
      console.error('Error exporting feedbacks:', err);
      setError(err.message || 'Failed to export feedbacks');
    } finally {
      setExporting(false);
    }
  };

  const handleViewFeedback = (feedbackId) => {
    const fb = feedbacks.find((f) => f.id === feedbackId) || null;
    if (fb) {
      // Use full email and comment for modal
      setSelectedFeedback({
        ...fb,
        email: fb.fullEmail || fb.email,
        comment: fb.fullComment || fb.comment
      });
    }
  };

  const handleCloseModal = () => setSelectedFeedback(null);

  useEffect(() => {
    if (!selectedFeedback) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleCloseModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedFeedback]);

  // No need for client-side filtering since API handles language pair filtering
  const filteredFeedbacks = feedbacks;

  // Use stats from API response instead of calculating from current page
  const stats = useMemo(() => {
    const positive = apiStats.total_feedback_liked || 0;
    const negative = apiStats.total_feedback_disliked || 0;
    const total = apiStats.total_feedback || 0;
    const approvalRate = total > 0 ? Math.round((positive / total) * 100) : 0;
    return { positive, negative, approvalRate };
  }, [apiStats]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Feedback Details Modal */}
      {selectedFeedback && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Feedback Details"
          onMouseDown={(e) => {
            // Close when clicking on overlay (not inside modal)
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Feedback Details</h3>
              <button
                type="button"
                onClick={handleCloseModal}
                className="h-9 w-9 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <span className="text-xl leading-none">×</span>
              </button>
            </div>

            <div className="px-4 sm:px-6 py-4 sm:py-6 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Source Language</div>
                  <div className="text-base text-gray-900">{selectedFeedback.sourceLanguage || "-"}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Target Language</div>
                  <div className="text-base text-gray-900">{selectedFeedback.targetLanguage || "-"}</div>
                </div>
              </div>

              <div className="mt-5 sm:mt-7">
                <div className="text-sm font-medium text-gray-700 mb-2">Input Text</div>
                <div className="w-full rounded-xl bg-gray-100 px-3 sm:px-4 py-3 sm:py-4 text-gray-900 text-sm sm:text-base break-words">
                  {selectedFeedback.inputText || "-"}
                </div>
              </div>

              <div className="mt-4 sm:mt-6">
                <div className="text-sm font-medium text-gray-700 mb-2">Output Text</div>
                <div className="w-full rounded-xl bg-gray-100 px-3 sm:px-4 py-3 sm:py-4 text-gray-900 text-sm sm:text-base break-words">
                  {selectedFeedback.outputText || "-"}
                </div>
              </div>



              <div className="mt-4 sm:mt-6">
                <div className="text-sm font-medium text-gray-700 mb-2">Comment</div>
                <div className="w-full rounded-xl bg-gray-100 px-3 sm:px-4 py-3 sm:py-4 text-gray-900 text-sm sm:text-base break-words">
                  {selectedFeedback.comment || "-"}
                </div>
              </div>

              <div className="mt-6 sm:mt-8 flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {loading ? '...' : stats.positive}
              </div>
              <div className="text-sm text-gray-500">Positive Feedbacks</div>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <ThumbsUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {loading ? '...' : stats.negative}
              </div>
              <div className="text-sm text-gray-500">Negative Feedbacks</div>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <ThumbsDown className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {loading ? '...' : `${stats.approvalRate}%`}
              </div>
              <div className="text-sm text-gray-500">Approval Rates</div>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <span className="text-xl">⭐</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">User Feedback</h2>

        <button
          onClick={handleExportAll}
          disabled={exporting || loading}
          className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary/80 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Export All
            </>
          )}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search Bar */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search feedback"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Filter Dropdowns */}
        <select
          value={filters.rating}
          onChange={(e) => handleFilterChange('rating', e.target.value)}
          className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm min-w-[150px]"
        >
          <option value="">All Ratings</option>
          <option value="positive">Positive</option>
          <option value="negative">Negative</option>
        </select>
        <select
          value={filters.platform}
          onChange={(e) => handleFilterChange('platform', e.target.value)}
          className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm min-w-[150px]"
        >
          <option value="">All Platforms</option>
          <option value="Web">Web</option>
          <option value="iOS">iOS</option>
          <option value="android">Android</option>
        </select>
        <select
          value={filters.languagePair}
          onChange={(e) => handleFilterChange('languagePair', e.target.value)}
          className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm min-w-[180px]"
        >
          <option value="">All Language Pairs</option>
          <option value="/ttt/v1/english-to-yoruba">English → Yoruba</option>
          <option value="/ttt/v1/english-to-swahili">English → Swahili</option>
          <option value="/ttt/v1/english-to-nigerian-pidgin">English → Nigerian Pidgin</option>
          <option value="/ttt/v1/yoruba-to-english">Yoruba → English</option>
          <option value="/ttt/v1/swahili-to-english">Swahili → English</option>
          <option value="/ttt/v1/nigerian-pidgin-to-english">Nigerian Pidgin → English</option>
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-600">Loading feedbacks...</span>
          </div>
        ) : filteredFeedbacks.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">No feedbacks found</p>
          </div>
        ) : (
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Name</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Email</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Rating</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Platform</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Comment</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Date</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredFeedbacks.map((feedback) => (
                <tr key={feedback.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-4 text-sm text-gray-900">{feedback.name}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">{feedback.email}</td>
                  <td className="py-4 px-4">
                    {feedback.rating === "positive" ? (
                      <ThumbsUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <ThumbsDown className="w-5 h-5 text-red-600" />
                    )}
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-900">{feedback.platform}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">{feedback.comment}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">{feedback.date}</td>
                  <td className="py-4 px-4">
                    <button
                      onClick={() => handleViewFeedback(feedback.id)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredFeedbacks.length > 0 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, pagination.total || 0)} of {pagination.total || 0} results
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {pagination.totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages || 1))}
                disabled={currentPage === (pagination.totalPages || 1)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecentFeedbacks;
